import type { InvoiceLineItem, InvoiceLineItemGroup } from '~/types'

/** Non-empty `id` — safe to merge/update rows by id after optimistic edits. */
export function hasInvoiceLineItemId(
  item: Pick<InvoiceLineItem, 'id'>,
): boolean {
  return typeof item.id === 'string' && item.id.trim().length > 0
}

/**
 * Match two bundle (or flat) line rows: by id when both persisted, else logId,
 * else reference equality (unsaved / optimistic rows).
 */
export function invoiceLineItemsMatchByIdOrRef(
  a: InvoiceLineItem,
  b: InvoiceLineItem,
): boolean {
  if (hasInvoiceLineItemId(a) && hasInvoiceLineItemId(b)) {
    return a.id === b.id
  }
  if (a.logId && b.logId && a.logId === b.logId) {
    return true
  }
  return a === b
}

export function getBundleChildIndexInParent(
  parent: InvoiceLineItem,
  child: InvoiceLineItem,
): number {
  return (parent.items || []).findIndex((c) =>
    invoiceLineItemsMatchByIdOrRef(c, child),
  )
}

export function findBundleParentInGroup(
  group: InvoiceLineItemGroup,
  child: InvoiceLineItem,
): InvoiceLineItem | null {
  const parent = group.groupedItems.find(
    (gi) =>
      Boolean(gi.items?.length) &&
      gi.items!.some((c) => invoiceLineItemsMatchByIdOrRef(c, child)),
  )
  return parent ?? null
}

export function isBundleChildInGroup(
  group: InvoiceLineItemGroup,
  item: InvoiceLineItem,
): boolean {
  return group.groupedItems.some(
    (gi) =>
      Boolean(gi.items?.length) &&
      gi.items!.some((c) => invoiceLineItemsMatchByIdOrRef(c, item)),
  )
}
